import React, { useState, useEffect } from 'react';
import { Target, CheckCircle2, XCircle, Loader2, TrendingUp, TrendingDown, Clock, Wrench, ChevronDown, ChevronUp, Bot } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Reasonable target AFRs by tune family — used to seed the corrected-tune
// build. User can still edit before hitting Derive.
const TARGET_AFR_BY_VERSION = {
  V9: 11.5, V10: 11.5, V11: 11.5, V11_BestOfBoth: 11.5,
  V12_SAFE: 11.0, V13_ALPHAN: 10.5,
};

// Map a scorecard's `version` string to the on-disk base bin name. Falls
// back to a V11 build if we don't have a precise match (caller can still
// change the base in the dialog).
const VERSION_TO_FILENAME = {
  V9: 'BMW_E90_328xi_E85_V9.bin',
  V10: 'BMW_E90_328xi_E85_V10.bin',
  V11: 'BMW_E90_328xi_E85_V11.bin',
  V11_BestOfBoth: 'BMW_E90_328xi_E85_V11.bin',
  V12_SAFE: 'BMW_E90_328xi_E85_V12_SAFE.bin',
  V13_ALPHAN: 'BMW_E90_328xi_E85_V13_AlphaN.bin',
};

function Metric({ label, pred, actual, unit = '', better = 'higher' }) {
  if (typeof pred !== 'number' || typeof actual !== 'number') return null;
  const delta = actual - pred;
  const good = better === 'higher' ? delta >= 0 : delta <= 0;
  return (
    <div className="flex items-center justify-between py-1 px-2 rounded-sm bg-[#050505] border border-[#1a1a1a]">
      <span className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">{label}</span>
      <div className="flex items-center gap-2 text-[10px] font-['JetBrains_Mono']">
        <span className="text-zinc-400">{pred.toFixed(pred < 20 ? 2 : 0)}{unit}</span>
        <span className="text-zinc-700">→</span>
        <span className={good ? 'text-emerald-400' : 'text-red-400'}>
          {actual.toFixed(actual < 20 ? 2 : 1)}{unit}
        </span>
        <span className={`text-[9px] ${good ? 'text-emerald-500/70' : 'text-red-500/70'}`}>
          ({delta >= 0 ? '+' : ''}{delta.toFixed(delta < 20 ? 2 : 1)})
        </span>
      </div>
    </div>
  );
}

// AIBuildSuggestion — owns the "Why this scale?" reasoning expander so its
// state (loading / response / error) is local. Calls /api/ai/derive/explain
// which is a stateless one-shot Claude prompt that doesn't touch chat history.
function AIBuildSuggestion({ version, actualAfr, onGenerate }) {
  const targetAfr = TARGET_AFR_BY_VERSION[version] || 11.5;
  const scaleFactor = actualAfr / targetAfr;
  const deltaPct = (scaleFactor - 1) * 100;
  const baseFilename = VERSION_TO_FILENAME[version] || 'BMW_E90_328xi_E85_V11.bin';
  const directionLabel = deltaPct > 0 ? 'richer' : 'leaner';

  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [reasoning, setReasoning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReasoning = async () => {
    setReasoningOpen(true);
    if (reasoning || loading) return;  // already loaded / in flight
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/ai/derive/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_filename: baseFilename,
          mods: [{
            id: 'afr_correction',
            params: { target_afr: targetAfr, actual_afr: actualAfr },
          }],
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || `HTTP ${res.status}`);
      } else {
        setReasoning(body.explanation || '');
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-2 bg-[#FF5E1A]/5 border border-[#FF5E1A]/30 rounded-sm" data-testid={`generate-from-insight-${version}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] text-[#FF5E1A] font-bold uppercase tracking-wider mb-0.5">
            AI Build Suggestion
          </p>
          <p className="text-[10px] text-zinc-300 leading-relaxed">
            Datalog AFR <span className="font-bold text-white font-['JetBrains_Mono']">{actualAfr.toFixed(2)}</span>
            {' vs target '}
            <span className="font-bold text-white font-['JetBrains_Mono']">{targetAfr.toFixed(2)}</span>.
            Scale fuel pulsewidth by{' '}
            <span className="font-bold text-white font-['JetBrains_Mono']">×{scaleFactor.toFixed(3)}</span>
            {' '}({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}% — {directionLabel}).
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onGenerate({
              base_filename: baseFilename,
              mods: [{
                id: 'afr_correction',
                params: { target_afr: targetAfr, actual_afr: actualAfr },
              }],
            });
          }}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white text-[10px] font-bold rounded-sm shadow-[0_0_12px_rgba(255,94,26,0.4)]"
          data-testid={`generate-tune-from-insight-btn-${version}`}
        >
          <Wrench className="w-3 h-3" />Generate Tune
        </button>
      </div>

      {/* Reasoning toggle — calls Claude on first open and caches the result. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (reasoningOpen) {
            setReasoningOpen(false);
          } else {
            fetchReasoning();
          }
        }}
        className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#FF5E1A]/80 hover:text-[#FF5E1A] font-medium"
        data-testid={`reasoning-toggle-${version}`}
      >
        <Bot className="w-3 h-3" />
        {reasoningOpen ? 'Hide Claude\u2019s reasoning' : 'Show Claude\u2019s reasoning'}
        {reasoningOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {reasoningOpen && (
        <div className="mt-2 p-2 bg-[#050505] border border-[#27272A] rounded-sm" data-testid={`reasoning-panel-${version}`}>
          {loading && (
            <div className="flex items-center gap-2 text-[10px] text-zinc-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Claude is reasoning about the build…
            </div>
          )}
          {error && (
            <p className="text-[10px] text-red-400">
              {error} <button onClick={(e) => { e.stopPropagation(); setError(null); setReasoning(null); fetchReasoning(); }} className="underline ml-1">Retry</button>
            </p>
          )}
          {reasoning && !loading && (
            <p className="text-[10px] text-zinc-300 leading-relaxed whitespace-pre-wrap">{reasoning}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function TuneScorecard({ version, onGenerateFromInsight }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!version) return;
    setLoading(true);
    fetch(`${API_URL}/api/tune-scorecard/${version}`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [version]);

  if (loading) {
    return (
      <div className="mt-3 p-3 bg-[#050505] border border-zinc-800 rounded-sm flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />
        <span className="text-[10px] text-zinc-500">Loading scorecard…</span>
      </div>
    );
  }
  if (!data) return null;

  const unverified = data.status === 'unverified';
  const sourceLabel = {
    user_datalog: 'LIVE FROM YOUR DATALOG',
    hardcoded_baseline: 'BASELINE REFERENCE',
    none: 'AWAITING FIRST FLASH',
  }[data.source] || null;

  return (
    <div className={`mt-3 p-3 rounded-sm border space-y-3 ${
      unverified ? 'bg-zinc-900/40 border-zinc-700/50'
        : data.verdict === 'HIT' ? 'bg-emerald-500/5 border-emerald-500/30'
        : 'bg-red-500/5 border-red-500/30'
    }`} data-testid={`scorecard-${version}`}>
      <div className="flex items-center gap-2">
        <Target className={`w-3.5 h-3.5 ${
          unverified ? 'text-zinc-500'
            : data.verdict === 'HIT' ? 'text-emerald-400' : 'text-red-400'
        }`} />
        <span className="text-xs font-bold text-white">Prediction Accuracy Scorecard</span>
        {sourceLabel && (
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-sm ${
            data.source === 'user_datalog'
              ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
              : data.source === 'hardcoded_baseline'
                ? 'bg-zinc-700/40 text-zinc-300 border border-zinc-600/40'
                : 'bg-zinc-800/40 text-zinc-500 border border-zinc-700/40'
          }`}>
            {sourceLabel}
          </span>
        )}
        {unverified ? (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-zinc-500">
            <Clock className="w-3 h-3" />UNVERIFIED
          </span>
        ) : data.verdict === 'HIT' ? (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400 font-bold">
            <CheckCircle2 className="w-3 h-3" />HIT
          </span>
        ) : (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-red-400 font-bold">
            <XCircle className="w-3 h-3" />MISS
          </span>
        )}
      </div>

      <p className="text-[11px] text-zinc-300 leading-relaxed">{data.headline}</p>

      {/* Health gate verdict inline (when available from live datalog) */}
      {data.health_gate_verdict && (
        <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[10px] border ${
          data.health_gate_verdict === 'READY_TO_TUNE'
            ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-300'
            : data.health_gate_verdict === 'MUST_FIX_FIRST'
              ? 'bg-red-500/5 border-red-500/30 text-red-300'
              : 'bg-amber-500/5 border-amber-500/30 text-amber-300'
        }`}>
          <span className="font-bold">HEALTH GATE:</span>
          <span>{data.health_gate_verdict.replace(/_/g, ' ')}</span>
          {data.health_gate_headline && <span className="opacity-70">— {data.health_gate_headline}</span>}
        </div>
      )}

      {data.actual?.source && data.source === 'user_datalog' && (
        <div className="flex items-center gap-1 text-[9px] text-cyan-400/80 font-['JetBrains_Mono']">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
          source: {data.actual.source}
          {data.uploaded_at && (
            <span className="text-zinc-600 ml-1">
              · {new Date(data.uploaded_at).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {unverified ? (
        <div className="p-2 bg-cyan-500/5 border border-cyan-500/20 rounded-sm">
          <p className="text-[10px] text-cyan-200/90 italic leading-relaxed">
            💡 <strong>Auto-tag tip:</strong> Include the version in your datalog filename (e.g. <code className="text-cyan-300 bg-black/40 px-1 rounded">DataLog_V11_Apr21.csv</code>) and the scorecard will populate automatically on upload — no extra clicks.
          </p>
        </div>
      ) : (
        <>
          {/* Headline metrics */}
          {data.actual && (
            <div className="grid grid-cols-2 gap-1.5">
              <div className="p-2 bg-[#050505] border border-[#1a1a1a] rounded-sm">
                <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Peak HP</p>
                <p className="text-lg font-bold text-white font-['JetBrains_Mono']">
                  {typeof data.actual.peak_hp === 'number' ? data.actual.peak_hp.toFixed(1) : '—'}
                  <span className="text-[10px] text-zinc-500 ml-1">@ {data.actual.peak_rpm ?? '—'} RPM</span>
                </p>
              </div>
              <div className="p-2 bg-[#050505] border border-[#1a1a1a] rounded-sm">
                <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Peak AFR</p>
                <p className="text-lg font-bold text-white font-['JetBrains_Mono']">
                  {typeof data.actual.peak_afr === 'number' ? data.actual.peak_afr.toFixed(2) : '—'}
                  <span className="text-[10px] text-zinc-500 ml-1">(knock {data.actual.knock_peak ?? '—'}V)</span>
                </p>
              </div>
            </div>
          )}

          {/* Per-RPM deltas */}
          {data.per_rpm && data.per_rpm.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Per-RPM prediction vs actual</p>
              {data.per_rpm.map((r) => (
                <div key={r.rpm} className="space-y-0.5">
                  <div className="text-[10px] text-zinc-400 font-bold font-['JetBrains_Mono']">{r.rpm} RPM</div>
                  <Metric label="HP" pred={r.hp_predicted} actual={r.hp_actual} better="higher" />
                  <Metric label="AFR" pred={r.afr_predicted} actual={r.afr_actual} better="lower" />
                </div>
              ))}
              {data.afr_mean_error !== null && (
                <div className="flex justify-between pt-2 mt-2 border-t border-zinc-800 text-[10px] font-['JetBrains_Mono']">
                  <span className="text-zinc-500">Mean AFR error:</span>
                  <span className={data.afr_mean_error < 0.5 ? 'text-emerald-400' : data.afr_mean_error < 1.5 ? 'text-amber-400' : 'text-red-400'}>
                    {data.afr_mean_error.toFixed(2)}
                  </span>
                </div>
              )}
              {data.hp_mean_error !== null && (
                <div className="flex justify-between text-[10px] font-['JetBrains_Mono']">
                  <span className="text-zinc-500">Mean HP error:</span>
                  <span className={data.hp_mean_error < 10 ? 'text-emerald-400' : data.hp_mean_error < 25 ? 'text-amber-400' : 'text-red-400'}>
                    {data.hp_mean_error.toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Insight */}
          {data.insight && (
            <div className="p-2 bg-amber-500/5 border border-amber-500/20 rounded-sm">
              <div className="flex items-start gap-1.5">
                {data.hp_mean_error > 20 ? <TrendingDown className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                  : <TrendingUp className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />}
                <p className="text-[10px] text-amber-200/90 leading-relaxed">
                  <strong>Model insight:</strong> {data.insight}
                </p>
              </div>
            </div>
          )}

          {/* Generate Tune From Insight — auto-build a corrected derived tune
              using the AFR observed in this datalog vs the version's target. */}
          {data.actual?.peak_afr && onGenerateFromInsight && (
            <AIBuildSuggestion
              version={version}
              actualAfr={Number(data.actual.peak_afr)}
              onGenerate={onGenerateFromInsight}
            />
          )}

          {data.actual?.source && data.source !== 'user_datalog' && (
            <p className="text-[9px] text-zinc-600 font-['JetBrains_Mono'] text-right">
              source: {data.actual.source}
            </p>
          )}
        </>
      )}
    </div>
  );
}
